import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';

/**
 * v2 dropdown — the styled listbox for choices the native `Select` in Field
 * cannot render: rows with a description under the label, a check on the
 * current value, our own focus ring and type-ahead. Field's `Select` is still
 * the right control for a plain list of strings; this one exists where the
 * options carry meaning the browser's menu cannot show.
 *
 * The menu is anchored in pure CSS — `absolute` under a `relative` wrapper —
 * and never by measured coordinates. The app sets `zoom` on the root, which
 * silently rescales any `position: fixed` offset a script computes, so a
 * measured menu lands beside its trigger rather than under it. CSS anchoring
 * has no such gap to get wrong.
 *
 * Keyboard: Enter/Space/↓/↑ open, ↑/↓ move, Home/End jump, Enter/Space commit,
 * Escape closes and returns focus to the trigger, Tab closes. Printable keys
 * jump to the next option starting with them.
 */
export interface DropdownOption {
  value: string;
  label: string;
  /** Second line under the label — what the choice actually means. */
  description?: string;
}

export interface DropdownProps {
  options: DropdownOption[];
  value: string;
  onChange: (value: string) => void;
  /** Shown on the trigger when `value` matches no option. */
  placeholder?: string;
  disabled?: boolean;
  /** Required when no visible <label> points at the trigger. */
  ariaLabel?: string;
  /**
   * Extra classes for the wrapper, which both the trigger and the menu fill.
   * Size the dropdown here (`w-[132px]`) rather than reaching for the trigger.
   */
  className?: string;
}

export const Dropdown: React.FC<DropdownProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select…',
  disabled = false,
  ariaLabel,
  className = '',
}) => {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const typeAhead = useRef<{ query: string; at: number }>({ query: '', at: 0 });
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const selectedIndex = useMemo(() => options.findIndex((o) => o.value === value), [options, value]);
  const selected = selectedIndex >= 0 ? options[selectedIndex] : null;
  const listId = React.useId();

  const openMenu = useCallback(() => {
    if (disabled) return;
    setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);
    setOpen(true);
  }, [disabled, selectedIndex]);

  const closeMenu = useCallback((refocus = true) => {
    setOpen(false);
    if (refocus) triggerRef.current?.focus();
  }, []);

  const commit = useCallback(
    (index: number) => {
      const option = options[index];
      if (option) onChange(option.value);
      closeMenu();
    },
    [options, onChange, closeMenu],
  );

  // Focus moves into the list so screen readers announce the active option and
  // Escape has somewhere to return from.
  useEffect(() => {
    if (open) listRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    listRef.current?.querySelector('[data-active="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [open, activeIndex]);

  const onListKeyDown = (event: React.KeyboardEvent) => {
    const last = options.length - 1;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, last));
        return;
      case 'ArrowUp':
        event.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
        return;
      case 'Home':
        event.preventDefault();
        setActiveIndex(0);
        return;
      case 'End':
        event.preventDefault();
        setActiveIndex(last);
        return;
      case 'Enter':
      case ' ':
        event.preventDefault();
        commit(activeIndex);
        return;
      case 'Escape':
        // Stopped here so a dropdown open inside a modal closes itself first,
        // rather than taking the whole modal down with it.
        event.preventDefault();
        event.stopPropagation();
        closeMenu();
        return;
      case 'Tab':
        closeMenu(false);
        return;
      default:
        break;
    }
    if (event.key.length !== 1) return;
    // Type-ahead: keys within a second build one query, so "ed" finds "Editor"
    // rather than jumping to whatever starts with "d".
    const now = Date.now();
    const query = (now - typeAhead.current.at < 1000 ? typeAhead.current.query : '') + event.key.toLowerCase();
    typeAhead.current = { query, at: now };
    const match = options.findIndex((o) => o.label.toLowerCase().startsWith(query));
    if (match >= 0) setActiveIndex(match);
  };

  const onTriggerKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openMenu();
    }
  };

  return (
    <div className={`relative ${className}`}>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label={ariaLabel}
        onClick={() => (open ? closeMenu(false) : openMenu())}
        onKeyDown={onTriggerKeyDown}
        className={
          'inline-flex h-8 w-full items-center justify-between gap-s2 rounded-control border bg-surface px-s4 ' +
          'text-body-md text-ink transition-colors duration-100 ' +
          'border-line hover:border-line-strong focus:outline-none focus-visible:ring-2 focus-visible:ring-action-border ' +
          'focus-visible:border-action disabled:opacity-50 disabled:bg-canvas'
        }
      >
        <span className={`truncate ${selected ? '' : 'text-ink-muted'}`}>{selected?.label ?? placeholder}</span>
        <ChevronDown className={`h-[13px] w-[13px] shrink-0 text-ink-muted transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <>
          {/* Click-away. Sits under the menu and over everything else, so one
              click outside closes without also hitting what it landed on. */}
          <button
            type="button"
            aria-hidden="true"
            tabIndex={-1}
            className="fixed inset-0 z-40 cursor-default"
            onMouseDown={() => closeMenu()}
          />
          <ul
            ref={listRef}
            id={listId}
            role="listbox"
            tabIndex={-1}
            aria-label={ariaLabel}
            aria-activedescendant={`${listId}-${activeIndex}`}
            onKeyDown={onListKeyDown}
            // The menu is at least as wide as the trigger, and wider when the
            // descriptions need it — never narrower, and never clipped to a
            // width that would truncate every row.
            className="absolute right-0 top-[calc(100%+4px)] z-50 m-0 max-h-[264px] w-max min-w-full max-w-[280px] list-none overflow-y-auto rounded-card border border-line bg-surface p-s2 shadow-floating focus:outline-none"
          >
            {options.map((option, index) => (
              <li
                key={option.value}
                id={`${listId}-${index}`}
                role="option"
                aria-selected={option.value === value}
                data-active={index === activeIndex}
                onMouseEnter={() => setActiveIndex(index)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  commit(index);
                }}
                className={
                  'flex cursor-pointer items-start gap-s3 rounded-control px-s4 py-s3 ' +
                  (index === activeIndex ? 'bg-action-bg' : '')
                }
              >
                <span className="min-w-0 flex-1">
                  <span className="block text-row-title text-ink">{option.label}</span>
                  {option.description && (
                    <span className="mt-[2px] block text-meta text-ink-secondary">{option.description}</span>
                  )}
                </span>
                {option.value === value && <Check className="mt-[2px] h-[13px] w-[13px] shrink-0 text-action" />}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
};

export default Dropdown;
